import ImageWithFallback from "@shared/components/Molecules/ImageWithFallback/ImageWithFallback.component";
import { useTranslations } from "@shared/context/useTranslations";
import { useEffect, useRef, useState } from "react";
import { fetchUserAccount } from "@shared/helpers/apis/userAccountHelper";
import InputField from "@shared/components/Atoms/InputField/InputField.component";
import { DeliveryMode, User } from "@shared/types/UserAccountTypes.interface";
import { BasketSummary, fetchBasketSummary } from "@shared/helpers/checkoutHelper";
import { DeliveryOptions, fetchDeliveryOptions } from "@shared/helpers/apis/deliveryOptionsHelper";
import { formatNumber } from "@shared/helpers/formatPrice";
import Button from "@shared/components/Atoms/Button/Button";
import { getRoutePathByPageType } from "@shared/helpers/findPathForSystemSite";
import { useNavigate } from "react-router-dom";
import { CheckoutData, sendOrder } from "@shared/helpers/apis/apiOrder";
import { useBasket } from "@shared/context/BasketContext";
import { usePage } from "@shared/context/PageContext";
import EmptyState from "@shared/components/Atoms/EmptyState/EmptyState.component";
import { IconNames } from "@shared/enums/icons.enums";
import ShowMore from "@shared/components/Molecules/ShowMore/ShowMore.component";
import { CheckoutContent } from "@shared/types/ContentTypes";
import { getThumbnailImageUrl } from "@shared/helpers/apis/productHelper";
import {
    DeliveryAddress,
    DeliveryAddressRef,
} from "./components/DeliveryAddress/DeliveryAddress.component";
import { UserAddressState } from "@shared/components/Molecules/AddressEdit/AddressEdit.component";
import { useAddress } from "@shared/context/AddressContext";
import { mapToCheckoutAddress } from "@shared/helpers/apis/addressBookHelper";
import { PageLoader } from "@shared/components/Atoms/PageLoader/PageLoader.component";
import { cn } from "@shared/helpers/classNames.helper";
import S from "./Checkout.module.scss";
import { Contacts, ContactsRef } from "./components/Contacts/Contacts.component";
import { useFreightDocument } from "@shared/hooks/useFreightDocument";
import { FreightDocumentLink } from "@shared/components/Molecules/FreightDocumentLink/FreightDocumentLink.component";

export const Checkout = () => {
    const { getTranslation } = useTranslations();
    const [userAccount, setUserAccount] = useState<User | null>(null);
    const [basketSummary, setBasketSummary] = useState<BasketSummary | null>(null);
    const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptions | null>(null);
    const [defaultDeliveryMode, setDefaultDeliveryMode] = useState<DeliveryMode | null>(null);
    const [activeDeliveryMode, setActiveDeliveryMode] = useState<string | null>(null);
    const [activeDeliveryType, setActiveDeliveryType] = useState<string | null>(null);
    const [checkoutComment, setCheckoutComment] = useState<string>("");
    const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
    const [isSameAddressChecked, setIsSameAddressChecked] = useState(true);
    const [basketId, setBasketId] = useState("");
    const [requisitionNumber, setRequisitionNumber] = useState("");
    const [customerReference, setCustomerReference] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { refetchBasket, basket } = useBasket();
    const { page } = usePage();
    const { refetchAddresses } = useAddress();

    const hasUnavailableItems =
        basket?.lines?.some((item) => item.lifecycle?.isUnavailable) ?? false;

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [submitError, setSubmitError] = useState<string>("");
    const [unavailableError, setUnavailableError] = useState<string>("");
    const [deliveryError, setDeliveryError] = useState<string>("");
    const [deliveryTypeError, setDeliveryTypeError] = useState<string>("");

    const [hidePostalCode, setHidePostalCode] = useState<boolean>(false);
    const [showState, setShowState] = useState<boolean>(false);

    const receiptPath = getRoutePathByPageType("Receipt") || "/receipt";

    const termsConditionsPath =
        (page as CheckoutContent).props?.termsConditions?.route.path || "/termscondition-page";
    const navigate = useNavigate();

    const ref = useRef<DeliveryAddressRef>(null);
    const contactRef = useRef<ContactsRef>(null);

    // @ts-expect-error
    const customer = globalThis?.customer;
    const customerNumber = customer?.customerNumber;
    const customerEmail = customer?.customerEmail || "";

    // @ts-expect-error
    const currentCulture = globalThis?.currentCulture;

    const freightDocumentUrl = useFreightDocument(currentCulture);

    useEffect(() => {
        setIsLoading(true);
        const fetchData = async () => {
            try {
                //Fetch user
                const userAccountData = await fetchUserAccount();
                setUserAccount(userAccountData);

                //Fetch all delivery options
                const deliveryData = await fetchDeliveryOptions(currentCulture);
                setDeliveryOptions(deliveryData);

                //Default delivery based on user
                const defaultMode = userAccountData.defaultDeliveryMode;
                setDefaultDeliveryMode(defaultMode);

                if (defaultMode) {
                    const matchingDeliveryCode = Object.entries(deliveryData || {}).find(
                        ([key, value]) => key && value === defaultMode.deliveryCodeDescription
                    )?.[0];
                    if (matchingDeliveryCode) {
                        setActiveDeliveryMode(matchingDeliveryCode);
                    }
                }

                //Fetch basket
                const basketData = await fetchBasketSummary(customerNumber, currentCulture);
                setBasketSummary(basketData);
                setBasketId(basketData.id);
                setIsLoading(false);
            } catch (error) {
                console.error("Failed to fetch data", error);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Place default delivery at top
    const getDeliveryOptionsList = () => {
        if (!deliveryOptions) return [];

        const orderedOptions = Object.entries(deliveryOptions).sort(([keyA], [keyB]) => {
            if (defaultDeliveryMode?.deliveryCode === keyA) return -1;
            if (defaultDeliveryMode?.deliveryCode === keyB) return 1;
            return 0;
        });
        return orderedOptions;
    };

    const handleDeliveryModeClick = (deliveryCode: string) => {
        setActiveDeliveryMode(deliveryCode);
        setDeliveryError("");

        // Clear the specific field error when the user starts typing
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors["deliveryMode"];

            // If there are no more errors, clear the submitError as well
            if (Object.keys(newErrors).length === 0) {
                setSubmitError("");
            }

            return newErrors;
        });
    };

    const handleDeliveryTypeClick = (deliveryType: string) => {
        setActiveDeliveryType(deliveryType);
        setDeliveryTypeError("");

        // Clear the specific field error when the user starts typing
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors["deliveryType"];

            // If there are no more errors, clear the submitError as well
            if (Object.keys(newErrors).length === 0) {
                setSubmitError("");
            }

            return newErrors;
        });
    };

    useEffect(() => {
        if (userAccount) {
            if (
                userAccount.invoiceAddress.countryId === "HKG" ||
                userAccount.invoiceAddress.countryId === "ARE"
            ) {
                setHidePostalCode(true);
            }
            if (userAccount.invoiceAddress.stateName) {
                setShowState(true);
            }
        }
    }, [userAccount]);

    const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckoutComment(e.target.value);
    };

    const handleRequisitionNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRequisitionNumber(value);

        // Clear the specific field error when the user starts typing
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (value.trim()) {
                delete newErrors["requisitionNumber"]; // Clear the error if the field is not empty
            }

            // If there are no more errors, clear the submitError as well
            if (Object.keys(newErrors).length === 0) {
                setSubmitError("");
            }

            return newErrors;
        });
    };

    const handleCustomerReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCustomerReference(value);
    };

    const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsTermsAccepted(e.target.checked);
    };

    const handleCheckout = async () => {
        if (isSubmitting) return;

        const validationErrors: { [key: string]: string } = {};
        const inValidValidation: UserAddressState = {
            isValid: false,
            address: null,
            saveAddress: false,
        };

        const contactValidation = contactRef.current?.validate();
        const checkoutContact = contactValidation?.isValid
            ? `${contactValidation.contact?.firstName} ${contactValidation.contact?.lastName}`
            : "";

        const addressValidation = isSameAddressChecked
            ? inValidValidation
            : ref.current?.validate() || inValidValidation;

        if (!requisitionNumber) {
            validationErrors.requisitionNumber = getTranslation("checkout.errorRequisitionNumber");
        }

        if (!activeDeliveryMode) {
            validationErrors.deliveryMode = getTranslation("checkout.errorDeliveryMode");
        }

        if (basketSummary?.requiresDeliveryChoice && !activeDeliveryType) {
            validationErrors.deliveryType = getTranslation("checkout.errorDeliveryType");
        }

        if (!contactValidation?.isValid) {
            const contactErrors = contactValidation?.errors;
            if (contactErrors && Object.keys(contactErrors).length > 0) {
                Object.entries(contactErrors).forEach(([field, error]) => {
                    validationErrors[`contact.${field}`] =
                        `${getTranslation("contactList.title")} - ${error}`;
                });
            } else {
                // No selection was made from the contact list, so there are no per-field errors to surface
                validationErrors.contact = getTranslation("contactList.errorNoItemSelected");
            }
        }

        if (
            Object.keys(validationErrors).length > 0 ||
            (!isSameAddressChecked && !addressValidation.isValid)
        ) {
            setErrors(validationErrors);
            setSubmitError(getTranslation("checkout.fixErrors"));
            if (validationErrors.deliveryMode) {
                setDeliveryError(validationErrors.deliveryMode);
            }
            if (validationErrors.deliveryType) {
                setDeliveryTypeError(validationErrors.deliveryType);
            }
            return;
        }

        setErrors({});
        setSubmitError("");
        setDeliveryError("");
        setDeliveryTypeError("");

        const checkoutData: CheckoutData = {
            basketId,
            customerEmail,
            deliveryCode: activeDeliveryMode,
            comment: checkoutComment,
            requisitionNumber: requisitionNumber,
            customerRef: customerReference,
            useInvoiceAddress: isSameAddressChecked,
            saveAddress: addressValidation.saveAddress,
            address: isSameAddressChecked
                ? undefined
                : mapToCheckoutAddress(addressValidation.address!),
            attentionPerson: checkoutContact,
            phone: contactValidation?.contact?.phone || userAccount?.phone || "",
            contactEmail: contactValidation?.contact?.email || userAccount?.email || "",
            deliveryType: activeDeliveryType || undefined,
        };

        // Set isSubmitting to true when the order process starts
        setIsSubmitting(true);

        try {
            // Save contact if needed before sending order
            if (contactValidation?.saveContact) {
                const contactToSave = {
                    ...contactValidation.contact!,
                    id: undefined,
                    isPrimary: false,
                };
                contactRef.current?.updateContact(contactToSave);
            }

            await sendOrder(checkoutData, currentCulture);
            navigate(receiptPath);
            refetchBasket();
            refetchAddresses();
        } catch (error: unknown) {
            console.error("Failed to send order:", error);
            const axiosError = error as {
                response?: { status?: number; data?: { message?: string } };
            };
            if (axiosError?.response?.status === 400 && axiosError?.response?.data?.message) {
                setUnavailableError(axiosError.response.data.message);
                refetchBasket();
            } else {
                setSubmitError(getTranslation("checkout.submitError"));
            }
        } finally {
            // Set isSubmitting back to false after completion or failure
            setIsSubmitting(false);
        }
    };

    if (!isLoading && !basketSummary?.lines) {
        return (
            <>
                <EmptyState
                    iconName={IconNames.Register}
                    heading={getTranslation("Checkout.emptyState.heading")}
                    description={getTranslation("checkout.emptyStateDescription")}
                />
            </>
        );
    }

    return (
        <>
            <PageLoader isPageLoading={isLoading} />
            <div className={S.wrapper}>
                <div className={S.wrapperMain}>
                    <div className={S.box}>
                        <div className={S.invoiceInfo}>
                            <span className={S.boxName}>
                                {getTranslation("checkout.InvoiceInfo")}
                            </span>
                            <div className={S.invoiceInfoStatic}>
                                <div className={S.userAccount}>
                                    <div
                                        className={cn(
                                            S.inputFields,
                                            hidePostalCode && S.hidePostalCode,
                                            showState && S.showState
                                        )}
                                    >
                                        {userAccount?.email && (
                                            <InputField
                                                className={S.inputField1}
                                                disabled
                                                value={userAccount?.email}
                                                label={getTranslation("userAccount.email")}
                                            />
                                        )}
                                        {userAccount?.vatNumber && (
                                            <InputField
                                                className={S.inputField2}
                                                disabled
                                                value={userAccount?.vatNumber}
                                                label={getTranslation("userAccount.vatNumber")}
                                            />
                                        )}
                                        {userAccount?.name && (
                                            <InputField
                                                className={S.inputField3}
                                                disabled
                                                value={userAccount?.name}
                                                label={getTranslation("userAccount.companyName")}
                                            />
                                        )}
                                        {userAccount?.phone && (
                                            <InputField
                                                className={S.inputField4}
                                                disabled
                                                value={userAccount?.phone}
                                                label={getTranslation("userAccount.phone")}
                                            />
                                        )}
                                        {userAccount?.invoiceAddress?.streetName && (
                                            <InputField
                                                className={S.inputField5}
                                                disabled
                                                value={userAccount?.invoiceAddress?.streetName}
                                                label={getTranslation("userAccount.streetName")}
                                            />
                                        )}
                                        {!hidePostalCode && (
                                            <InputField
                                                className={S.inputField6}
                                                disabled
                                                value={
                                                    userAccount?.invoiceAddress?.postalCode ?? ""
                                                }
                                                label={getTranslation("userAccount.postalCode")}
                                            />
                                        )}
                                        {userAccount?.invoiceAddress?.cityName && (
                                            <InputField
                                                className={S.inputField7}
                                                disabled
                                                value={userAccount?.invoiceAddress?.cityName}
                                                label={getTranslation("userAccount.city")}
                                            />
                                        )}
                                        {showState && (
                                            <InputField
                                                className={S.inputField10}
                                                disabled
                                                value={userAccount?.invoiceAddress?.stateName ?? ""}
                                                label={getTranslation("userAccount.state")}
                                            />
                                        )}
                                        {userAccount?.invoiceAddress.countryId && (
                                            <InputField
                                                className={S.inputField9}
                                                disabled
                                                value={userAccount?.invoiceAddress.countryId}
                                                label={getTranslation("userAccount.country")}
                                            />
                                        )}
                                        <InputField
                                            minLength={1}
                                            maxLength={20}
                                            className={S.inputField8}
                                            value={requisitionNumber}
                                            onChange={handleRequisitionNumberChange}
                                            required
                                            placeholder={getTranslation(
                                                "checkout.errorRequisitionNumber.placeholder"
                                            )}
                                            error={errors.requisitionNumber}
                                            label={getTranslation("checkout.requisitionNumber")}
                                            errorRelative
                                        />
                                        <InputField
                                            maxLength={60}
                                            className={S.inputField14}
                                            value={customerReference}
                                            onChange={handleCustomerReferenceChange}
                                            placeholder={getTranslation(
                                                "checkout.customerReference.placeholder"
                                            )}
                                            label={getTranslation("checkout.customerReference")}
                                        />
                                        <InputField
                                            maxLength={60}
                                            className={S.inputField14}
                                            value={customerReference}
                                            onChange={handleCustomerReferenceChange}
                                            placeholder={getTranslation(
                                                "checkout.customerReference.placeholder"
                                            )}
                                            label={getTranslation("checkout.customerReference")}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={S.invoiceInfoAlternative}>
                                <div className={S.invoiceAddressCheckbox}>
                                    <label>
                                        <input
                                            className={S.checkbox}
                                            type="checkbox"
                                            checked={isSameAddressChecked}
                                            onChange={() =>
                                                setIsSameAddressChecked(!isSameAddressChecked)
                                            }
                                            aria-checked={isSameAddressChecked}
                                        />
                                        {getTranslation("checkout.deliveryAddressAsInvoice")}
                                    </label>
                                </div>

                                {!isSameAddressChecked ? (
                                    <div className={S.deliveryAddressInfo}>
                                        <DeliveryAddress ref={ref} />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className={`${S.box} ${deliveryError ? S.errorBorder : ""}`}>
                        <div className={S.delivery}>
                            <span className={S.boxName}>{getTranslation("checkout.Delivery")}</span>
                            <div className={S.deliveryList}>
                                <ShowMore
                                    buttonTextBefore={getTranslation("Checkout.buttonTextBefore")}
                                    buttonTextAfter={getTranslation("Checkout.buttonTextAfter")}
                                >
                                    {getDeliveryOptionsList().map(([key, value]) => (
                                        <button
                                            key={key}
                                            className={`${S.deliveryModeItem} ${
                                                activeDeliveryMode === key ? S.active : ""
                                            }`}
                                            onClick={() => handleDeliveryModeClick(key)}
                                        >
                                            <span className={S.chooseIcon}></span>
                                            <span>{value}</span>
                                            {defaultDeliveryMode?.deliveryCode === key && (
                                                <p className={S.defaultDelivery}>
                                                    {getTranslation("Checkout.defaultDelivery")}
                                                </p>
                                            )}
                                            {/* 17 equals to express delivery */}
                                            {key === "17" && (
                                                <p className={S.expressDelivery}>
                                                    {getTranslation("Checkout.expressDelivery")}
                                                </p>
                                            )}
                                        </button>
                                    ))}
                                </ShowMore>
                            </div>
                            {deliveryError && <div className={S.errorMessage}>{deliveryError}</div>}
                        </div>
                    </div>

                    {basketSummary?.requiresDeliveryChoice ? (
                        <div className={S.box}>
                            <span className={cn(S.boxName, S.boxWithDescription)}>
                                {getTranslation("checkout.DeliveryType")}
                            </span>
                            <div className={S.boxDescription}>
                                {getTranslation("checkout.DeliveryTypeDescription")}
                            </div>
                            <div>
                                <button
                                    className={`${S.deliveryTypeItem} ${
                                        activeDeliveryType === "full delivery" ? S.active : ""
                                    }`}
                                    onClick={() => handleDeliveryTypeClick("full delivery")}
                                >
                                    <span className={S.chooseIcon}></span>
                                    <div className={S.deliveryTypeInfo}>
                                        <span>{getTranslation("checkout.DeliveryTypeFull")}</span>
                                        <p>
                                            {getTranslation("checkout.DeliveryTypeFullDescription")}
                                        </p>
                                    </div>
                                </button>
                                <button
                                    className={`${S.deliveryTypeItem} ${
                                        activeDeliveryType === "partial delivery" ? S.active : ""
                                    }`}
                                    onClick={() => handleDeliveryTypeClick("partial delivery")}
                                >
                                    <span className={S.chooseIcon}></span>
                                    <div className={S.deliveryTypeInfo}>
                                        <span>
                                            {getTranslation("checkout.DeliveryTypePartial")}
                                        </span>
                                        <p>
                                            {getTranslation(
                                                "checkout.DeliveryTypePartialDescription"
                                            )}
                                        </p>
                                    </div>
                                </button>
                            </div>

                            {deliveryTypeError && (
                                <div className={S.errorMessage}>{deliveryTypeError}</div>
                            )}
                        </div>
                    ) : null}

                    <div className={S.box}>
                        <div className={S.contacts}>
                            <Contacts ref={contactRef} />
                        </div>
                    </div>

                    <div className={S.box}>
                        <div className={S.comment}>
                            <span className={S.boxName}>{getTranslation("checkout.Comments")}</span>
                            <InputField
                                className={S.inputField1}
                                value={checkoutComment}
                                label={getTranslation("checkout.comment")}
                                onChange={handleCommentChange}
                                placeholder={getTranslation("checkout.commentPlaceholder")}
                            />
                            {page?.props?.additionalText ? (
                                <div
                                    className={S.commentInfo}
                                    dangerouslySetInnerHTML={{
                                        __html: page?.props?.additionalText?.markup,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className={S.box}>
                        <div className={S.approve}>
                            <span className={S.boxName}>{getTranslation("checkout.Approve")}</span>
                            <div className={S.acceptTradeConditions}>
                                <label>
                                    <input
                                        className={S.checkbox}
                                        type="checkbox"
                                        checked={isTermsAccepted}
                                        onChange={handleTermsChange}
                                        aria-checked={isTermsAccepted}
                                    />
                                    <span className={S.termWrapper}>
                                        <span className={S.termText}>
                                            {getTranslation("checkout.TermsTexts")}
                                        </span>
                                        <Button
                                            ariaLabel={getTranslation(
                                                "Checkout.ariaLabel.termsConditions"
                                            )}
                                            onClick={() => {
                                                window.open(termsConditionsPath, "_blank");
                                            }}
                                            className={S.goToListBtn}
                                            variant="transparent"
                                            size="small"
                                        >
                                            {getTranslation("checkout.Terms")}
                                        </Button>
                                    </span>
                                </label>
                            </div>
                            <Button
                                onClick={handleCheckout}
                                disabled={!isTermsAccepted || isSubmitting || hasUnavailableItems}
                                className={S.checkoutButton}
                                variant="basket"
                                type="submit"
                            >
                                {getTranslation("checkout.CompleteOrder")}
                            </Button>
                            {hasUnavailableItems && (
                                <div className={S.unavailableWarning}>
                                    {getTranslation("checkout.unavailable-warning")}
                                </div>
                            )}
                            {unavailableError && (
                                <div className={S.errorMessage}>{unavailableError}</div>
                            )}
                            {(submitError || deliveryError) && (
                                <div className={S.errorMessage}>
                                    <span className={S.fixErrors}>
                                        {getTranslation("checkout.fixErrors")}
                                    </span>
                                    <ul>
                                        {Object.values(errors).map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={S.wrapperSub}>
                    <div className={S.orderSummery}>
                        <span className={S.orderSummeryTitle}>
                            {getTranslation("checkout.OrderSummery")}
                        </span>
                        {basketSummary?.lines.map((line) => (
                            <div
                                key={line.id}
                                className={cn(
                                    S.orderSummeryItem,
                                    line.lifecycle?.isUnavailable && S.orderSummeryItemUnavailable
                                )}
                            >
                                <div className={S.orderSummeryItemImage}>
                                    <ImageWithFallback
                                        src={getThumbnailImageUrl(line.images[0]?.url)}
                                        showFallback={line.images?.length === 0}
                                        alt={line.name}
                                        className={S.mainImageContainer}
                                    />
                                </div>
                                <div className={S.orderSummeryInfo}>
                                    <span className={S.orderSummeryInfoTitle}>
                                        {line.productId}
                                    </span>
                                    <span className={S.orderSummeryInfoName}>{line.name}</span>
                                    {line.lifecycle?.isUnavailable ? (
                                        <span className={S.orderSummeryUnavailableTag}>
                                            {getTranslation("product.unavailable")}
                                        </span>
                                    ) : (
                                        <span className={S.orderSummeryInfoAmount}>
                                            {`${line.quantity} ${getTranslation(`checkout.${line?.data?.Unit}`)}`}
                                        </span>
                                    )}
                                </div>
                                {!line.lifecycle?.isUnavailable && (
                                    <div className={S.orderSummeryPrice}>
                                        <div
                                            className={cn(
                                                S.orderSummeryPriceItem,
                                                line.data.discountedPrice &&
                                                    S.orderSummeryPriceItemListPrice
                                            )}
                                        >
                                            {`${formatNumber(line.data.listPrice, currentCulture, {
                                                decimals: 2,
                                            })}
                                            ${line.data.currencyCode?.toUpperCase()}`}
                                        </div>
                                        {line.data.discountedPrice &&
                                            line.data.discountedPrice > 0 && (
                                                <>
                                                    <div
                                                        className={S.orderSummeryPriceItemDiscount}
                                                    >
                                                        -
                                                        {`${formatNumber(
                                                            line.data.discount,
                                                            currentCulture,
                                                            {
                                                                decimals: 2,
                                                            }
                                                        )}
                                                    ${line.data.currencyCode?.toUpperCase()}`}
                                                    </div>
                                                    <div className={S.orderSummeryPriceItem}>
                                                        {`${formatNumber(
                                                            line.data.discountedPrice,
                                                            currentCulture,
                                                            {
                                                                decimals: 2,
                                                            }
                                                        )}
                                                    ${line.data.currencyCode?.toUpperCase()}`}
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className={S.orderSummeryTotal}>
                            <span className={S.orderSummeryTotalText}>
                                {getTranslation("checkout.subTotal")}
                            </span>
                            <div className={S.orderSummeryTotalValue}>
                                <span className={S.orderSummeryTotalValueItem}>
                                    {`${formatNumber(basketSummary?.subTotal, currentCulture, {
                                        decimals: 2,
                                    })} ${basketSummary?.currencyCode?.toUpperCase()}`}
                                </span>
                            </div>
                        </div>

                        <div className={S.orderSummeryTotal}>
                            <span className={S.orderSummeryTotalText}>
                                {getTranslation("checkout.totalDiscount")}
                            </span>
                            <div className={S.orderSummeryTotalValue}>
                                <span className={S.orderSummeryTotalValueItem}>
                                    -
                                    {`${formatNumber(basketSummary?.totalDiscount, currentCulture, {
                                        decimals: 2,
                                    })} ${basketSummary?.currencyCode?.toUpperCase()}`}
                                </span>
                            </div>
                        </div>

                        <div className={S.orderSummeryTotal}>
                            <span className={S.orderSummeryTotalText}>
                                {getTranslation("checkout.netTotal")}
                            </span>
                            <div className={S.orderSummeryTotalValue}>
                                <span className={S.orderSummeryTotalValueItem}>
                                    {`${formatNumber(basketSummary?.netTotal, currentCulture, {
                                        decimals: 2,
                                    })} ${basketSummary?.currencyCode?.toUpperCase()}`}
                                </span>
                            </div>
                        </div>

                        {basketSummary?.charges?.map((charge, index) => (
                            <div className={S.orderSummeryCharge} key={index}>
                                <span className={S.orderSummeryChargeText}>
                                    {getTranslation(`basket.chargesTitle${charge.markupCode}`)}
                                </span>
                                <div className={S.orderSummeryChargeValue}>
                                    <span className={S.orderSummeryChargeValueItem}>
                                        {`${formatNumber(charge.chargeAmount, currentCulture, {
                                            decimals: 2,
                                        })} ${basketSummary?.currencyCode?.toUpperCase()}`}
                                    </span>
                                </div>
                            </div>
                        ))}

                        <div className={cn(S.orderSummeryTotal, S.orderSummeryTotalBold)}>
                            <span className={S.orderSummeryTotalText}>
                                {getTranslation("checkout.TotalExDelivery")}
                            </span>
                            <div className={S.orderSummeryTotalValue}>
                                <span className={S.orderSummeryTotalValueItem}>
                                    {`${formatNumber(basketSummary?.totalPrice, currentCulture, {
                                        decimals: 2,
                                    })} ${basketSummary?.currencyCode?.toUpperCase()}`}
                                </span>
                            </div>
                        </div>
                        <div className={S.freightBoxWrapper}>
                            <div className={S.orderSummeryTotalDeliverInfo}>
                                <span className={S.orderSummeryTotalDeliverInfoName}>
                                    {getTranslation("checkout.DeliveryCost")}
                                </span>
                                <span className={S.orderSummeryTotalDeliverInfoValue}>
                                    {getTranslation("checkout.AddedAfterOrder")}
                                </span>
                            </div>
                            <FreightDocumentLink url={freightDocumentUrl} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
